import React, { useState } from 'react'
import _, { isEmpty } from 'lodash'
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Switch
} from 'antd'
import { ButtonEx } from '../../../../styles/button.style'
import { useDeepEffect } from '../../../../utils/hooks'

const BOOLEAN_FIELDS = [
  { name: 'alertSetting', label: 'Alert Setting' },
  { name: 'sentEmailReport', label: 'Sent Email Report' },
  { name: 'aiAccess', label: 'AI Access' },
  { name: 'watermarkRemoved', label: 'Watermark Removed' },
  { name: 'priceScraper', label: 'Price Scraper' },
  { name: 'liveDashboard', label: 'Live Dashboard' },
  { name: 'toolAccess', label: 'Tool Access' },
  { name: 'auctionAccess', label: 'Auction Access' },
  { name: 'recycleNews', label: 'Recycle News' },
  { name: 'shopLayout', label: 'Shop Layout 3D' },
  { name: 'tracking', label: 'Tracking' },
  { name: 'qcMaxImage', label: 'QC Max Image (6 pics)' },
  { name: 'cashDrawer', label: 'Cash Drawer' },
  { name: 'customMaterial', label: 'Custom Material' }
]

// defaultMode / defaultValue: ค่าที่ใช้เมื่อสร้าง tier ใหม่ หรือ backend ยังไม่ส่งช่องนั้นมา
const NUMBER_FIELDS = [
  { name: 'limitWeighing', label: 'Limit Weighing' },
  { name: 'limitAccounting', label: 'Limit Accounting' },
  // จำนวนวัสดุที่ร้านสร้างเองได้ (green2get-app spec 111) — 0 = ไม่จำกัด ค่าเริ่มต้น 50
  {
    name: 'limitCustomMaterial',
    label: 'Limit Custom Material',
    defaultMode: 'custom',
    defaultValue: 50
  }
]

const modeOf = (field, value) => {
  if (value === 0) {
    return 'unlimited'
  }
  if (value == null) {
    return field.defaultMode || 'unlimited'
  }
  return 'custom'
}

const limitModeOf = (formData) =>
  NUMBER_FIELDS.reduce(
    (modes, field) => ({
      ...modes,
      [field.name]: modeOf(field, (formData || {})[field.name])
    }),
    {}
  )

// limitCustomerTag ตีความต่างจาก limitWeighing / limitAccounting ในตารางเดียวกัน
// ที่นี่ -1 = ไม่จำกัด, 0 = สร้างเองไม่ได้เลย, n = n ป้าย
// เพราะ tier FREE ต้องได้ศูนย์ ซึ่งเขียนไม่ได้ภายใต้ convention 0 = unlimited (spec 082 D-17)
const tagModeOf = (value) => {
  if (value === -1) return 'unlimited'
  if (value === 0 || value == null) return 'disabled'
  return 'custom'
}

const tagValueOf = (mode, value) => {
  if (mode === 'unlimited') return -1
  if (mode === 'disabled') return 0
  return value
}

// จำนวนกล้องเสริมในหน้าชั่งของแอป LOMA (green2get-app spec 143) — 0 = ใช้ไม่ได้ แอปรับสูงสุด 3
const SECONDARY_CAMERA_OPTIONS = [0, 1, 2, 3]

const TierFeatureForm = (props) => {
  const { visible, onOk, onCancel, formData, existingTiers, form } = props
  const { getFieldDecorator, validateFields } = form
  const isEdit = !_.isEmpty(formData)

  const [limitMode, setLimitMode] = useState(limitModeOf({}))
  const [tagMode, setTagMode] = useState('disabled')

  useDeepEffect(() => {
    if (!isEmpty(formData)) {
      form.setFieldsValue({
        tier: formData.tier,
        alertSetting: !!formData.alertSetting,
        sentEmailReport: !!formData.sentEmailReport,
        aiAccess: !!formData.aiAccess,
        watermarkRemoved: !!formData.watermarkRemoved,
        priceScraper: !!formData.priceScraper,
        liveDashboard: !!formData.liveDashboard,
        toolAccess: !!formData.toolAccess,
        auctionAccess: !!formData.auctionAccess,
        recycleNews: !!formData.recycleNews,
        shopLayout: !!formData.shopLayout,
        tracking: !!formData.tracking,
        qcMaxImage: formData.qcImage === 6,
        cashDrawer: !!formData.cashDrawer,
        customMaterial: !!formData.customMaterial,
        secondaryCamera: formData.secondaryCamera || 0
      })
      setTagMode(tagModeOf(formData.limitCustomerTag))
      setLimitMode(limitModeOf(formData))
    } else {
      setLimitMode(limitModeOf({}))
      // tier ใหม่ตั้งต้นที่ "สร้างเองไม่ได้" เพราะ fail-closed ปลอดภัยกว่าเผลอแจกไม่จำกัด
      setTagMode('disabled')
    }

    return () => {
      form.resetFields()
    }
  }, [formData])

  const onChangeLimitMode = (field, mode) => {
    setLimitMode((prev) => ({ ...prev, [field]: mode }))
  }

  function onSubmit(e) {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (!err) {
        const data = {
          tier: values.tier.trim().toUpperCase(),
          alertSetting: !!values.alertSetting,
          limitWeighing:
            limitMode.limitWeighing === 'unlimited' ? 0 : values.limitWeighing,
          limitAccounting:
            limitMode.limitAccounting === 'unlimited'
              ? 0
              : values.limitAccounting,
          sentEmailReport: !!values.sentEmailReport,
          aiAccess: !!values.aiAccess,
          watermarkRemoved: !!values.watermarkRemoved,
          priceScraper: !!values.priceScraper,
          liveDashboard: !!values.liveDashboard,
          toolAccess: !!values.toolAccess,
          // เครื่องมือขายทอดตลาดแยก flag จาก toolAccess — @NotNull เช่นกัน
          auctionAccess: !!values.auctionAccess,
          // เครื่องมือข่าวรีไซเคิลมี flag ของตัวเอง (green2get-app spec 105)
          recycleNews: !!values.recycleNews,
          // เครื่องมือจัดผังร้าน 3D มี flag ของตัวเอง (green2get-app spec 121)
          shopLayout: !!values.shopLayout,
          tracking: !!values.tracking,
          qcImage: values.qcMaxImage ? 6 : 1,
          limitCustomerTag: tagValueOf(tagMode, values.limitCustomerTag),
          // backend ประกาศเป็น @NotNull — ไม่ส่งไปจะโดนปฏิเสธทั้งฟอร์ม
          cashDrawer: !!values.cashDrawer,
          // วัสดุเฉพาะร้าน (green2get-app spec 111) — backend รับสองช่องนี้แบบไม่บังคับ
          customMaterial: !!values.customMaterial,
          limitCustomMaterial:
            limitMode.limitCustomMaterial === 'unlimited'
              ? 0
              : values.limitCustomMaterial,
          // กล้องเสริมของแอป LOMA (green2get-app spec 143) — backend รับแบบไม่บังคับ
          secondaryCamera: values.secondaryCamera
        }
        onOk(data)
      }
    })
  }

  return (
    <Modal
      title={`${isEdit ? 'Edit' : 'Create'} Tier`}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width={600}>
      <Form form={form} onSubmit={onSubmit} layout="vertical">
        <Form.Item label="Tier">
          {getFieldDecorator(`tier`, {
            rules: [
              {
                required: true,
                message: 'Please enter tier name'
              },
              {
                validator: (rule, value, callback) => {
                  if (
                    !isEdit &&
                    value &&
                    (existingTiers || [])
                      .map((t) => t.toUpperCase())
                      .includes(value.trim().toUpperCase())
                  ) {
                    callback(new Error('Tier already exists'))
                  } else {
                    callback()
                  }
                }
              }
            ]
          })(<Input placeholder="e.g. FREE, PRO, MAX" disabled={isEdit} />)}
        </Form.Item>

        {NUMBER_FIELDS.map((field) => (
          <Form.Item
            key={field.name}
            label={field.label}
            extra={
              field.name === 'limitCustomMaterial' &&
              !form.getFieldValue('customMaterial')
                ? 'จะมีผลเมื่อเปิด Custom Material'
                : undefined
            }>
            <Radio.Group
              value={limitMode[field.name]}
              onChange={(e) => onChangeLimitMode(field.name, e.target.value)}
              style={{ marginBottom: 8 }}>
              <Radio value="unlimited">ไม่จำกัด</Radio>
              <Radio value="custom">ระบุจำนวน</Radio>
            </Radio.Group>
            {limitMode[field.name] === 'custom' &&
              getFieldDecorator(field.name, {
                initialValue:
                  formData && formData[field.name]
                    ? formData[field.name]
                    : field.defaultValue || 1,
                rules: [
                  {
                    required: true,
                    message: 'Please enter a value'
                  }
                ]
              })(
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Enter value"
                />
              )}
          </Form.Item>
        ))}

        <Form.Item label="Limit Customer Tag (ป้ายกำกับที่ร้านสร้างเอง)">
          <Radio.Group
            value={tagMode}
            onChange={(e) => setTagMode(e.target.value)}
            style={{ marginBottom: 8 }}>
            <Radio value="unlimited">ไม่จำกัด</Radio>
            <Radio value="disabled">สร้างเองไม่ได้</Radio>
            <Radio value="custom">ระบุจำนวน</Radio>
          </Radio.Group>
          {tagMode === 'custom' &&
            getFieldDecorator('limitCustomerTag', {
              initialValue:
                formData && formData.limitCustomerTag > 0
                  ? formData.limitCustomerTag
                  : 1,
              rules: [{ required: true, message: 'Please enter a value' }]
            })(
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                placeholder="Enter value"
              />
            )}
          <div style={{ fontSize: 12, color: '#888' }}>
            "สร้างเองไม่ได้" ยังใช้ป้ายกำกับกลางได้ตามปกติ
            จำกัดเฉพาะการสร้างป้ายของร้าน
          </div>
        </Form.Item>

        <Form.Item label="Secondary Camera (กล้องเสริมในหน้าชั่ง LOMA)">
          {getFieldDecorator('secondaryCamera', {
            // tier ใหม่ตั้งต้นที่ 0 (ใช้ไม่ได้) เหมือน Limit Customer Tag
            initialValue: 0
          })(
            <Select style={{ width: '100%' }}>
              {SECONDARY_CAMERA_OPTIONS.map((count) => (
                <Select.Option key={count} value={count}>
                  {count === 0 ? '0 (ใช้ไม่ได้)' : `${count} กล้อง`}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Row gutter={16}>
          {BOOLEAN_FIELDS.map((field) => (
            <Col span={12} key={field.name}>
              <Form.Item label={field.label}>
                {getFieldDecorator(field.name, {
                  valuePropName: 'checked',
                  initialValue: false
                })(<Switch />)}
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Row type="flex" gutter={8} justify="end">
          <Col>
            <Button onClick={() => onCancel()}>Cancel</Button>
          </Col>
          <Col>
            <ButtonEx color="#6ebf4b" type="primary" htmlType="submit">
              Save
            </ButtonEx>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

const WrappedTierFeatureForm = Form.create({ name: 'TierFeatureForm' })(
  TierFeatureForm
)
export default WrappedTierFeatureForm
